import { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import UI, { SERVICES_EN, SEO_EN, SLUG_MAP_FR_TO_EN, SLUG_MAP_EN_TO_FR } from './translations';
import { SERVICES, SEO, TESTIMONIALS, COMPANY, CITIES } from '../constants';

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const location = useLocation();

  const value = useMemo(() => {
    const isEn = location.pathname === '/en' || location.pathname.startsWith('/en/');
    const lang = isEn ? 'en' : 'fr';
    const t = UI[lang];
    const services = lang === 'en' ? SERVICES_EN : SERVICES;
    const seo = lang === 'en' ? SEO_EN : SEO;

    // Build home path for this language
    const homePath = lang === 'en' ? '/en' : '/';

    // Build nav links (anchor links point to the correct homepage)
    const homeBase = lang === 'en' ? '/en' : '/';
    const navLinks = [
      { href: `${homeBase}#accueil`, label: t.nav.home },
      { href: `${homeBase}#services`, label: t.nav.services },
      { href: `${homeBase}#avis`, label: t.nav.reviews },
      { href: `${homeBase}#contact`, label: t.nav.contact },
      { href: `${homeBase}#devis`, label: t.nav.quote },
    ];

    // Build the alternate-language path for the current page (for hreflang & switcher)
    let altPath;
    if (lang === 'en') {
      // We're on an English page — build the French equivalent
      if (location.pathname === '/en') {
        altPath = '/';
      } else {
        const enSlug = location.pathname.replace('/en/', '');
        const frSlug = SLUG_MAP_EN_TO_FR[enSlug];
        altPath = frSlug ? `/${frSlug}` : '/';
      }
    } else {
      // We're on a French page — build the English equivalent
      if (location.pathname === '/') {
        altPath = '/en';
      } else {
        const frSlug = location.pathname.substring(1);
        const enSlug = SLUG_MAP_FR_TO_EN[frSlug];
        altPath = enSlug ? `/en/${enSlug}` : '/en';
      }
    }

    // Helper: prefix a path for the current language
    const localePath = (path) => {
      if (lang === 'en') {
        if (path === '/') return '/en';
        return `/en${path}`;
      }
      return path;
    };

    // Get service slug for linking
    const serviceLink = (service) => {
      return lang === 'en' ? `/en/${service.slug}` : `/${service.slug}`;
    };

    return {
      lang,
      t,
      services,
      seo,
      testimonials: TESTIMONIALS,
      company: COMPANY,
      cities: CITIES,
      homePath,
      navLinks,
      altPath,
      localePath,
      serviceLink,
    };
  }, [location.pathname]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider');
  return ctx;
}
